<template>
  <div class="container">
    <span>
      Busca el tipo de reporte y aplica filtros para encontrar los resultados que necesites.
      <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer">Click aquí</a> para conocer más.
    </span>
    <div class="header-select">
      <a-form layout="vertical" class="form-wrapper">
        <a-form-item label="Tipo de Reporte">
          <a-select
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            v-model:value="reportSelected"
            class="form-item-xl"
            placeholder="Seleccionar un tipo de reporte"
            show-search
            :filter-option="filterOption"
          >
            <a-select-option v-for="report in customerReports" :key="report?.code">
              {{ report?.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item
          v-if="hasSubtypes"
          :label="`Tipo de reporte ${SubtypesByReport[reportSelected]?.label}`"
        >
          <a-select
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
            v-model:value="selectedReportSubtype"
            class="form-item"
            placeholder="Seleccionar un tipo de reporte"
            :options="SubtypesByReport[reportSelected]?.options"
          />
        </a-form-item>
      </a-form>
      <div
        class="info-help"
        v-if="reportSelected && currentReportHelp"
      >
        <p class="info-help__text">
          {{ currentReportHelp.text }}
          Conoce más haciendo
          <a :href="currentReportHelp.url" target="_blank" rel="noopener noreferrer">click aquí</a>.
        </p>
      </div>

    </div>
    <div>
      <report-typifications
        :key="`${reportSelected}-1`"
        v-if="
          [
            1,
            9,
            10,
            ReportTypes.Sells,
            ReportTypes.Atc,
            ReportTypes.Forms,
            ReportTypes.ContactProducts,
          ].includes(reportSelected)
        "
        :typeReport="reportSelected"
      />
      <AtcMessaging v-else-if="reportSelected == ReportTypes.AtcMessaging" :typeReport="reportSelected" />
      <management-progress v-else-if="reportSelected == 2" />
      <typifications-by-hour v-else-if="reportSelected == 3" />
      <time-platform v-else-if="reportSelected == 4" />
      <multiple-reports
        :key="reportSelected"
        v-else-if="reportSelected > 4 && reportSelected < 7"
        :typeReport="reportSelected"
      />
      <InboundVoice v-if="reportSelected === ReportTypes.InboundVoice" />
      <CustomerSupport
        v-if="reportSelected === ReportTypes.CustomerSupport"
        :customerSupportType="selectedReportSubtype"
      />

      <PayPromise v-if="reportSelected === ReportTypes.PayPromise" :type="selectedReportSubtype" />

      <Calls v-if="reportSelected === ReportTypes.Calls" />

      <Ivr
        v-if="[ReportTypes.IvrAutomatic, ReportTypes.IvrSurvey].includes(reportSelected)"
        :report="reportSelected"
      />

      <MessagingTickets v-if="reportSelected === ReportTypes.MessagingTickets" />

      <Strategies v-if="reportSelected === ReportTypes.Strategies" />

      <Phones v-if="reportSelected === ReportTypes.Phones" />

      <LeadsResume v-if="reportSelected === ReportTypes.LeadsResume" />

      <CapturedLeads v-if="reportSelected === ReportTypes.CapturedLeads" />

      <MessageTicketsTransfer v-if="reportSelected === ReportTypes.MessageTicketsTransfer" />

      <calls-synlab
        :key="reportSelected"
        v-if="[ReportTypes.CallAttempts, ReportTypes.CallsAnswered, ReportTypes.IncomingPerAgent, ReportTypes.Callback].includes(reportSelected) "
        :typeReport="reportSelected"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";

import { ReportTypes, SubtypesByReport, ReportDocs } from "@/app/reports/utils/enums";
import { fetchCustomerAvailableReports } from "@/app/reports/services";

import ReportTypifications from "@/app/reports/components/reports/reportTypifications.vue";
import ManagementProgress from "@/app/reports/components/reports/managementProgress.vue";
import TypificationsByHour from "@/app/reports/components/reports/typificationsByHour.vue";
import TimePlatform from "@/app/reports/components/reports/timePlatform.vue";
import MultipleReports from "@/app/reports/components/reports/multipleReports.vue";
import InboundVoice from "@/app/reports/components/reports/InboundVoice.vue";
import CustomerSupport from "@/app/reports/components/reports/CustomerSupport/index.vue";
import PayPromise from "@/app/reports/components/reports/PayPromise/index.vue";
import Calls from "@/app/reports/components/reports/Calls.vue";
import Ivr from "../reports/Ivr.vue";
import MessagingTickets from "@/app/reports/components/reports/MessagingTickets.vue";
import Strategies from "@/app/reports/components/reports/Strategies.vue";
import Phones from "@/app/reports/components/reports/Phones.vue";
import CapturedLeads from "@/app/reports/components/reports/CapturedLeads.vue";
import LeadsResume from "@/app/reports/components/reports/LeadsResume.vue";
import MessageTicketsTransfer from "@/app/reports/components/reports/MessageTicketsTransfer.vue";
import CallsSynlab from "@/app/reports/components/reports/CallsSynlab.vue";
import AtcMessaging from "@/app/reports/components/reports/AtcMessaging.vue";

const hasSubtypes = computed(() => {
  const reportsWithSubtypes = [ReportTypes.CustomerSupport, ReportTypes.PayPromise];

  return reportsWithSubtypes.includes(reportSelected.value);
});

const filterOption = (input, option) => {
  const report = customerReports.value.find((report) => report.code === option.value);

  return report?.name?.toLowerCase()?.indexOf(input.toLowerCase()) >= 0;
};
const user_track = 1;
const reportSelected = ref(undefined);
const selectedReportSubtype = ref(undefined);

const customerReports = ref([]);

const DOCS_URL = `${import.meta.env.VITE_WEB_DOC}/reportes/`;
const DOCS_URL2 = `${import.meta.env.VITE_WEB_DOC}/informes/reportes`;

const currentReportHelp = computed(() => {
  const info = ReportDocs[reportSelected.value];
  if (!info) return null;
  return {
    url: `${DOCS_URL2}/${info.slug}`,
    text: info.text
  }
});

onMounted(async () => {
  if (user_track == localStorage.getItem("used_id")) {
    const url = new URL(window.location.href);
    url.searchParams.set("tce", "act");
    window.history.replaceState(null, "", url.href);
  }
  const response = await fetchCustomerAvailableReports();
  customerReports.value = response.results;
});

watch(reportSelected, () => {
  if (selectedReportSubtype.value) selectedReportSubtype.value = undefined;
});
</script>

<style lang="sass" scoped>
.container
  padding: 12px 20px
.header-select
  margin-top: 15px
  padding: 12px 15px
  width: 100%
  min-height: 90px
  background: #F0F5FF
  border-radius: 4px

  :deep(.ant-form-item)
    margin-bottom: 8px

.form
  &-wrapper
    display: flex
    gap: 8px

  &-item
    min-width: 240px

    &-xl
      min-width: 300px

.info-help
  &__text
    color: #000000A6
    margin-bottom: 0
</style>
